import React, { useState } from 'react';
import { DigisacService } from '../../services/digisacService';
import { useNotifier } from '../ui/Notifier';
import type { SendMessageForm, SendTemplateForm, CreateScheduleForm } from '../../types/digisac';

type Mode = 'message' | 'template' | 'schedule';

interface NewMessageModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const NewMessageModal: React.FC<NewMessageModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const notifier = useNotifier();
  const [mode, setMode] = useState<Mode>('message');
  const [loading, setLoading] = useState(false);

  // Shared
  const [phone, setPhone] = useState('');
  const [serviceId, setServiceId] = useState('');

  // Message
  const [message, setMessage] = useState('');

  // Template
  const [templateName, setTemplateName] = useState('');
  const [paramsRaw, setParamsRaw] = useState(''); // key=value lines

  // Schedule
  const [scheduledFor, setScheduledFor] = useState('');
  const [timezone, setTimezone] = useState('America/Sao_Paulo');
  const [scheduleMode, setScheduleMode] = useState<'message' | 'template'>('message');

  if (!isOpen) return null;

  const parseParams = (raw: string): Record<string, string> | undefined => {
    if (!raw.trim()) return undefined;
    const result: Record<string, string> = {};
    raw.split('\n').forEach(line => {
      const idx = line.indexOf('=');
      if (idx > 0) {
        result[line.slice(0, idx).trim()] = line.slice(idx + 1).trim();
      }
    });
    return Object.keys(result).length > 0 ? result : undefined;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!phone.trim()) {
      notifier.show({ message: 'Informe o telefone', severity: 'error' });
      return;
    }
    setLoading(true);
    try {
      if (mode === 'message') {
        const payload: SendMessageForm = {
          phone: phone.trim(),
          message: message.trim(),
          ...(serviceId.trim() ? { serviceId: serviceId.trim() } : {}),
        };
        await DigisacService.sendMessage(payload);
        notifier.show({ message: 'Mensagem enviada com sucesso!', severity: 'success' });
      } else if (mode === 'template') {
        const payload: SendTemplateForm = {
          phone: phone.trim(),
          templateName: templateName.trim(),
          parameters: parseParams(paramsRaw),
          ...(serviceId.trim() ? { serviceId: serviceId.trim() } : {}),
        };
        await DigisacService.sendTemplate(payload);
        notifier.show({ message: 'Template enviado com sucesso!', severity: 'success' });
      } else {
        if (!scheduledFor) {
          notifier.show({ message: 'Informe a data/hora do agendamento', severity: 'error' });
          setLoading(false);
          return;
        }
        const payload: CreateScheduleForm = {
          phone: phone.trim(),
          scheduledFor: new Date(scheduledFor).toISOString(),
          timezone: timezone.trim() || 'America/Sao_Paulo',
          ...(serviceId.trim() ? { serviceId: serviceId.trim() } : {}),
          ...(scheduleMode === 'template'
            ? { templateName: templateName.trim(), parameters: parseParams(paramsRaw) }
            : { message: message.trim() }),
        };
        await DigisacService.createSchedule(payload);
        notifier.show({ message: 'Agendamento criado com sucesso!', severity: 'success' });
      }
      onSuccess();
      onClose();
    } catch (err: any) {
      notifier.show({ message: err?.message ?? 'Erro ao enviar', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-sm font-semibold" style={{ color: '#3d2b56' }}>Nova Mensagem / Agendamento</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-lg leading-none">&times;</button>
        </div>

        {/* Mode tabs */}
        <div className="flex gap-1 mb-4">
          {(['message', 'template', 'schedule'] as Mode[]).map(m => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              className="flex-1 py-1.5 text-xs rounded font-medium transition-colors"
              style={mode === m
                ? { backgroundColor: '#7A61B2', color: '#fff' }
                : { backgroundColor: '#F3F0F9', color: '#3d2b56' }}
            >
              {m === 'message' ? 'Mensagem' : m === 'template' ? 'Template' : 'Agendar'}
            </button>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          {/* Phone */}
          <div>
            <label className="block text-xs font-medium mb-1" style={{ color: '#3d2b56' }}>
              Telefone (com DDD)
            </label>
            <input
              type="tel"
              value={phone}
              onChange={e => setPhone(e.target.value)}
              placeholder="(11) 99999-9999"
              required
              className="w-full border rounded px-3 py-1.5 text-xs focus:outline-none focus:ring-1"
              style={{ borderColor: '#D1C4E9' }}
            />
          </div>

          {/* Message text */}
          {(mode === 'message' || (mode === 'schedule' && scheduleMode === 'message')) && (
            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: '#3d2b56' }}>Mensagem</label>
              <textarea
                value={message}
                onChange={e => setMessage(e.target.value)}
                rows={3}
                required
                className="w-full border rounded px-3 py-1.5 text-xs focus:outline-none focus:ring-1 resize-none"
                style={{ borderColor: '#D1C4E9' }}
              />
            </div>
          )}

          {/* Template name + params */}
          {(mode === 'template' || (mode === 'schedule' && scheduleMode === 'template')) && (
            <>
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: '#3d2b56' }}>Nome do Template</label>
                <input
                  type="text"
                  value={templateName}
                  onChange={e => setTemplateName(e.target.value)}
                  required
                  placeholder="ex: bem_vindo"
                  className="w-full border rounded px-3 py-1.5 text-xs focus:outline-none focus:ring-1"
                  style={{ borderColor: '#D1C4E9' }}
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: '#3d2b56' }}>
                  Parâmetros (opcional, formato: chave=valor por linha)
                </label>
                <textarea
                  value={paramsRaw}
                  onChange={e => setParamsRaw(e.target.value)}
                  rows={2}
                  placeholder={"nome=João\nproduto=Seguro Auto"}
                  className="w-full border rounded px-3 py-1.5 text-xs focus:outline-none focus:ring-1 resize-none font-mono"
                  style={{ borderColor: '#D1C4E9' }}
                />
              </div>
            </>
          )}

          {/* Schedule-specific */}
          {mode === 'schedule' && (
            <>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setScheduleMode('message')}
                  className="flex-1 py-1 text-xs rounded"
                  style={scheduleMode === 'message'
                    ? { backgroundColor: '#7A61B2', color: '#fff' }
                    : { backgroundColor: '#F3F0F9', color: '#3d2b56' }}
                >
                  Texto livre
                </button>
                <button
                  type="button"
                  onClick={() => setScheduleMode('template')}
                  className="flex-1 py-1 text-xs rounded"
                  style={scheduleMode === 'template'
                    ? { backgroundColor: '#7A61B2', color: '#fff' }
                    : { backgroundColor: '#F3F0F9', color: '#3d2b56' }}
                >
                  Template
                </button>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: '#3d2b56' }}>Data / Hora</label>
                <input
                  type="datetime-local"
                  value={scheduledFor}
                  onChange={e => setScheduledFor(e.target.value)}
                  required
                  className="w-full border rounded px-3 py-1.5 text-xs focus:outline-none focus:ring-1"
                  style={{ borderColor: '#D1C4E9' }}
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: '#3d2b56' }}>Fuso horário</label>
                <input
                  type="text"
                  value={timezone}
                  onChange={e => setTimezone(e.target.value)}
                  className="w-full border rounded px-3 py-1.5 text-xs focus:outline-none focus:ring-1"
                  style={{ borderColor: '#D1C4E9' }}
                />
              </div>
            </>
          )}

          {/* Optional serviceId */}
          <div>
            <label className="block text-xs font-medium mb-1" style={{ color: '#9CA3AF' }}>
              Service ID (opcional)
            </label>
            <input
              type="text"
              value={serviceId}
              onChange={e => setServiceId(e.target.value)}
              placeholder="UUID da conexão WhatsApp"
              className="w-full border rounded px-3 py-1.5 text-xs focus:outline-none focus:ring-1"
              style={{ borderColor: '#E5E7EB' }}
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-1.5 text-xs rounded border"
              style={{ borderColor: '#D1C4E9', color: '#3d2b56' }}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-1.5 text-xs rounded text-white disabled:opacity-50"
              style={{ backgroundColor: '#7A61B2' }}
            >
              {loading ? 'Enviando...' : mode === 'schedule' ? 'Agendar' : 'Enviar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
